import { ColumnDef } from "@tanstack/react-table";
import { ChevronUp, ChevronDown } from "lucide-react";
import { Button, SortableColumnHeader } from "@pushpress/pushpress-ui";
import UserAvatar from "../../../../../components/common/UserAvatar/UserAvatar";
import { joinStrings } from "../../../../../utils/misc";
import { EMPTY_DATA, STRIPE_DASHBOARD_URL } from "../../../constants";
import {
  getMemberPayment,
  hasMultiplePaymentMethods,
  getBillingDataForMember,
  getStatusBadge,
} from "../../../helpers";
import {
  MemberDataType,
  StripeBillingDataType,
  MatchState,
} from "../../../types";
import { getMatchingRecordData } from "../shared/MatchingRecord";
import { useState } from "react";
import { EditableCell } from "./editable-cell";

const MatchReasonCell = ({
  matchingFeedback,
}: {
  matchingFeedback?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { reason } = getMatchingRecordData(matchingFeedback || "{}");

  if (!reason || reason === EMPTY_DATA) {
    return <>{EMPTY_DATA}</>;
  }

  return (
    <div className="tw-relative tw-max-w-[250px] tw-rounded-md tw-p-2 tw-bg-neutral-100">
      <div className={`${!isExpanded ? "tw-relative" : ""}`}>
        <p
          className={`tw-text-sm tw-text-neutral-600 tw-break-words tw-whitespace-normal ${
            !isExpanded ? "tw-max-h-[3.6em] tw-overflow-hidden" : ""
          }`}
        >
          {reason}
        </p>

        {!isExpanded && reason.length > 100 && (
          <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-8 tw-bg-gradient-to-t tw-from-neutral-100 tw-to-transparent"></div>
        )}
      </div>

      {reason.length > 100 && (
        <div
          className={`tw-flex tw-justify-center ${!isExpanded ? "tw-relative tw-mt-[-12px]" : "tw-mt-2"}`}
        >
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="tw-h-5 tw-w-5 tw-rounded-full tw-bg-white hover:tw-bg-neutral-200 tw-transition-colors tw-border tw-border-neutral-300 tw-flex tw-items-center tw-justify-center tw-shadow-sm"
          >
            {isExpanded ? (
              <ChevronUp className="tw-w-4 tw-h-4" />
            ) : (
              <ChevronDown className="tw-w-4 tw-h-4" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export const createConsolidatedColumns = ({
  stripeBillingData,
  setSelectedMemberId,
  setShowMultiPaymentsDrawer,
  handleRejectPaymentMethod,
  handleAcceptSinglePaymentMethod,
  handleUpdateStripeId,
  isFetchingStripeBillingData,
  createSingleBillingLoading,
  isMemberProfilePageEnabled,
}: {
  stripeBillingData: StripeBillingDataType["data"];
  setSelectedMemberId: (value: number) => void;
  setShowMultiPaymentsDrawer: (value: boolean) => void;
  handleRejectPaymentMethod: (userId: number) => void;
  handleAcceptSinglePaymentMethod: (member: MemberDataType) => void;
  handleUpdateStripeId: (data: { userId: number; stripeId: string }) => void;
  isFetchingStripeBillingData: boolean;
  createSingleBillingLoading: boolean;
  isMemberProfilePageEnabled: boolean;
}): ColumnDef<MemberDataType>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <SortableColumnHeader column={column} title="Name" />
    ),
    enableSorting: true,
    filterFn: (row, columnId, filterValue) => {
      const member = row.original;
      const searchValue = filterValue.toLowerCase();
      const fullName = joinStrings(
        member.firstName,
        member.lastName,
      ).toLowerCase();
      const firstName = (member.firstName || "").toLowerCase();
      const lastName = (member.lastName || "").toLowerCase();
      const email = (member.email || "").toLowerCase();

      return (
        fullName.includes(searchValue) ||
        firstName.includes(searchValue) ||
        lastName.includes(searchValue) ||
        email.includes(searchValue)
      );
    },
    enableColumnFilter: true,
    cell: ({ row }) => {
      const member = row.original;
      return (
        <a
          href={
            isMemberProfilePageEnabled
              ? `/user/${member.userUuid}`
              : `/members/view/${member.userUuid}`
          }
          rel="noopener noreferrer"
          target="_blank"
        >
          <div className="tw-flex tw-items-center">
            <div className="tw-mr-2">
              <UserAvatar data={member} size="small" />
            </div>
            <div>
              <p className="tw-text-neutral-800">
                {joinStrings(member.firstName, member.lastName)}
              </p>
              <p className="tw-text-neutral-600">{member.email || "—"}</p>
            </div>
          </div>
        </a>
      );
    },
    sortingFn: (rowA, rowB) => {
      const nameA = joinStrings(
        rowA.original.firstName,
        rowA.original.lastName,
      ).toLowerCase();
      const nameB = joinStrings(
        rowB.original.firstName,
        rowB.original.lastName,
      ).toLowerCase();
      return nameA.localeCompare(nameB);
    },
  },
  {
    accessorKey: "stripeEmail",
    header: ({ column }) => (
      <SortableColumnHeader column={column} title="Stripe Email" />
    ),
    enableSorting: true,
    cell: ({ row }) => {
      const member = row.original;
      if (typeof stripeBillingData === "string" || !member.stripeId) {
        return EMPTY_DATA;
      }
      return (
        <p className="tw-text-neutral-600">
          {stripeBillingData[member.stripeId]?.customerData.email || EMPTY_DATA}
        </p>
      );
    },
    sortingFn: (rowA, rowB) => {
      const emailA =
        typeof stripeBillingData === "string" || !rowA.original.stripeId
          ? ""
          : (
              stripeBillingData[rowA.original.stripeId]?.customerData.email ||
              ""
            ).toLowerCase();

      const emailB =
        typeof stripeBillingData === "string" || !rowB.original.stripeId
          ? ""
          : (
              stripeBillingData[rowB.original.stripeId]?.customerData.email ||
              ""
            ).toLowerCase();

      return emailA.localeCompare(emailB);
    },
  },
  {
    accessorKey: "payment",
    header: ({ column }) => (
      <SortableColumnHeader column={column} title="Payment" />
    ),
    enableSorting: true,
    cell: ({ row }) => {
      const member = row.original;
      return member.billingBrand && member.billingLastFour ? (
        <div className="tw-text-neutral-400">{getMemberPayment(member)}</div>
      ) : (
        <div className="tw-text-neutral-400">
          {getBillingDataForMember(stripeBillingData, member)}
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const memberA = rowA.original;
      const memberB = rowB.original;

      const paymentA =
        memberA.billingBrand && memberA.billingLastFour
          ? `${memberA.billingBrand} ${memberA.billingLastFour}`.toLowerCase()
          : "";

      const paymentB =
        memberB.billingBrand && memberB.billingLastFour
          ? `${memberB.billingBrand} ${memberB.billingLastFour}`.toLowerCase()
          : "";

      return paymentA.localeCompare(paymentB);
    },
  },
  {
    accessorKey: "matchState",
    header: ({ column }) => (
      <SortableColumnHeader column={column} title="Status" />
    ),
    enableSorting: true,
    cell: ({ row }) => {
      const member = row.original;
      return getStatusBadge(member.matchState ?? MatchState.UNMATCHED);
    },
    sortingFn: (rowA, rowB) => {
      const stateA = rowA.original.matchState ?? MatchState.UNMATCHED;
      const stateB = rowB.original.matchState ?? MatchState.UNMATCHED;

      const stateOrder = {
        unmatched: 0,
        rejected: 1,
        pending: 2,
        confirmed: 3,
      };

      return (stateOrder[stateA] ?? -1) - (stateOrder[stateB] ?? -1);
    },
    filterFn: (row, columnId, filterValue) => {
      const memberState = row.original.matchState ?? MatchState.UNMATCHED;
      return filterValue.includes(memberState);
    },
    meta: {
      filterVariant: "select",
    },
  },
  {
    accessorKey: "stripeId",
    header: ({ column }) => (
      <SortableColumnHeader column={column} title="Stripe ID" />
    ),
    enableSorting: true,
    cell: ({ row }) => {
      const member = row.original;
      return (
        <EditableCell
          value={member.stripeId || null}
          onSave={(newStripeId) => {
            handleUpdateStripeId({
              userId: member.id!,
              stripeId: newStripeId,
            });
          }}
          displayComponent={
            member.stripeId ? (
              <a
                href={`${STRIPE_DASHBOARD_URL}${member.stripeId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-text-blue-500"
              >
                {member.stripeId}
              </a>
            ) : (
              <span>{EMPTY_DATA}</span>
            )
          }
        />
      );
    },
  },
  {
    accessorKey: "matchConfidence",
    header: ({ column }) => (
      <SortableColumnHeader column={column} title="Match Confidence" />
    ),
    enableSorting: true,
    cell: ({ row }) => {
      const member = row.original;
      const { confidenceScore } = getMatchingRecordData(
        member.matchingFeedback || "{}",
      );
      return confidenceScore || EMPTY_DATA;
    },
    sortingFn: (rowA, rowB) => {
      const confidenceScoreA = getMatchingRecordData(
        rowA.original.matchingFeedback || "{}",
      ).confidenceScore;
      const confidenceScoreB = getMatchingRecordData(
        rowB.original.matchingFeedback || "{}",
      ).confidenceScore;

      const scoreA =
        confidenceScoreA === "—" ? -1 : parseInt(confidenceScoreA, 10);
      const scoreB =
        confidenceScoreB === "—" ? -1 : parseInt(confidenceScoreB, 10);

      return scoreB - scoreA;
    },
  },
  {
    accessorKey: "matchReason",
    header: ({ column }) => (
      <SortableColumnHeader column={column} title="Match Reason" />
    ),
    cell: ({ row }) => {
      const member = row.original;
      return <MatchReasonCell matchingFeedback={member.matchingFeedback} />;
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const member = row.original;

      if (member.matchState !== MatchState.UNMATCHED || member.stripeId) {
        return (
          <div className="tw-flex tw-flex-col tw-gap-2 md:tw-flex-row">
            <Button
              variant="outline"
              onClick={() => handleRejectPaymentMethod(member.id!)}
            >
              Reject
            </Button>
            <Button
              variant="default"
              onClick={() => {
                if (hasMultiplePaymentMethods(stripeBillingData, member)) {
                  setSelectedMemberId(member.id!);
                  setShowMultiPaymentsDrawer(true);
                } else {
                  handleAcceptSinglePaymentMethod(member);
                }
              }}
              disabled={isFetchingStripeBillingData}
              isSubmitting={createSingleBillingLoading}
            >
              Accept
            </Button>
          </div>
        );
      }

      return <></>;
    },
  },
];
